import Store from '../util/store'
import { MediaType, MediaOper, EventResult, RecvState, EventType } from "../../common/enums"
import { getAddrs, getEventPostfix, tracelog, checkParam } from "../../common/common"
import DownPeerConnection from "../peer-connection/down-peer-connection"
import UpPeerConnection from "../peer-connection/up-peer-connection"
import logger from "../../common/logger"
import videoManager from "./video-manager"
import audioManager from "./audio-manager"
import shareManager from "./share-manager"
import boardManager from "./white-board-manager"
import eventRegistry from '../../common/event-registry'
import MediaMapping from "./media-mapping"
import mediaMapping from './media-mapping'
import Element from './element-manage'

/**
 * 有些处理是多个媒体类型（音频、视频、屏幕共享、电子白板）所共有的
 */
class MediaManager {
  constructor() {
    this.init()
    // 订阅消息处理
    Store.msgDispatcher.addMsgHandler(14106, this.onNotifyUserMediaState.bind(this));
    Store.msgDispatcher.addMsgHandler(14109, this.onNotifyGroupMediaStates.bind(this));
    Store.msgDispatcher.addMsgHandler(14110, this.onNotifyUserJoinGroup.bind(this));
    Store.msgDispatcher.addMsgHandler(14111, this.onNotifyUserLeaveGroup.bind(this));
    Store.msgDispatcher.addMsgHandler(18000, this.onNotifySendStream.bind(this));
    Store.msgDispatcher.addMsgHandler(18003, this.onRecvStreamRes.bind(this));
    
  }
  init () {
    // BindId : UpPeerConnection
    this.upPcMap = new Map();

    Store.upPcMap = this.upPcMap

    // BindId : DownPeerConnection
    this.downPcMap = new Map();

    Store.downPcMap = this.downPcMap

    // To be restored down tracks 
    this.restoreDownTracks = new Array();

    // 缓存发布操作消息
    this.cachePublicMsg = new Map()

    // 缓存接收远程流消息
    this.cacheSendReceiveMap = new Map()

    // 保存远程渲染video对象
    this.elementMap = new Map()

    this.groupUsersMap = new Map()
  }

  @tracelog
  tryRestoreSendMsg () {
    // 恢复发布媒体消息
    this.cachePublicMsg.forEach((item) => {
      this.startPublishMedia(...item)
    })
    // 恢复接收媒体
    this.cacheSendReceiveMap.forEach((item) => {
      this.startReceiveMedia(...item)
    })
  }
  /**
   * 恢复媒体渲染
   * @param {*} params 
   * @returns 找到了track
   */
  @tracelog
  tryRestoreMediaRender(params) {
    if (this.restoreDownTracks.length == 0) {
      logger.log("Empty restore tracks.");
      return false;
    }
    let flag = false
    let index = null
    this.restoreDownTracks.map((track, index) => {
      if (params.userId === track.userId
        || params.mediaType === track.mediaType
        || params.mediaId === track.mediaId) { // 恢复流程
        if (track.videoEle) {
          this.setMediaRender(track.userId, track.mediaType, track.mediaId, track.videoEle);
        }
        flag = true
        index = index
      }
    })
    this.restoreDownTracks.splice(index, 1)      
    return flag
}

  /**
   * 接收媒体
   * @param {*} params 
   */
  _doStartReceiveMedia(params) {
    this.startReceiveMedia(params.userId, params.mediaType, params.mediaId).then(()=>{
      logger.log("Start receive media success.");
    }).catch((e)=>{
      logger.error("Start receive media failed, err: " + e);
      this.restoreDownTracks.shift();
    })
  }

  /**
   * 恢复接受媒体的事件处理
   * @param {*} params 
   */
  @tracelog
  restoreDownTrack(params) {
    let { userId, mediaType, mediaId } = params
    if (this.cacheSendReceiveMap.get(getEventPostfix(userId, mediaType, mediaId))) {
      this.restoreDownTracks.push(params);
      this._doStartReceiveMedia(params);
    } else {
      logger.log('Track is stoped.')
    }
  }

  /**
   * 开始广播本地媒体
   * @param {*} mediaType 
   * @param {*} mediaId 
   * @param {*} params
   * @returns 无
   */
  @tracelog
  startPublishMedia(mediaType, mediaId, params) {
    this.cachePublicMsg.set(getEventPostfix(null, mediaType, mediaId), [mediaType, mediaId, params])
    switch(mediaType) {
      case MediaType.AUDIO:
        audioManager.startPublishAudio(mediaId);
        break;
      case MediaType.VIDEO:
        videoManager.startPublishVideo(mediaId, params);
        break;
      case MediaType.SCREEN_SHARE:
        shareManager.startScreenShare(params);
        break;
      case MediaType.WHITE_BOARD:
        boardManager.startPublishWhiteBoard(mediaId);
        break;
      default:
        logger.error("Unknown media type: " + mediaType);
        break;
    }
  };

  /**
   * 停止广播本地媒体
   * @param {*} mediaType 
   * @param {*} mediaId
   * @returns 无 
   */
  @tracelog
  stopPublishMedia(mediaType, mediaId) {
    this.cachePublicMsg.delete(getEventPostfix(null, mediaType, mediaId))
    switch(mediaType) {
      case MediaType.AUDIO:
        audioManager.stopPublishAudio();
        break;
      case MediaType.VIDEO:
        videoManager.stopPublishVideo(mediaId);
        break;
      case MediaType.SCREEN_SHARE:
        shareManager.stopScreenShare();
        break;
      case MediaType.WHITE_BOARD:
        boardManager.stopPublishWhiteBoard(mediaId);
        break;
      default:
        logger.error("Unknown media type: " + mediaType);
        break;
    }
  };

  /**
   * 开始接收远端媒体，FIXME：需要返回Promise
   * @param {*} userId 
   * @param {*} mediaType 
   * @param {*} mediaId 
   * @returns Promise
   */
  @tracelog
  startReceiveMedia(userId, mediaType, mediaId) {   
    this.cacheSendReceiveMap.set(getEventPostfix(userId, mediaType, mediaId), [userId, mediaType, mediaId])
    switch(mediaType) {
      case MediaType.AUDIO:
        return audioManager.startReceiveAudio(userId, mediaId);
      case MediaType.VIDEO:
        return videoManager.startReceiveVideo(userId, mediaId);
      case MediaType.SCREEN_SHARE:
        return shareManager.startReceiveScreenShare(userId, mediaId);
      case MediaType.WHITE_BOARD:
        return boardManager.startReceiveWhiteBoard(userId, mediaId);
      default:
        return new Promise((resolve, reject) => {
          reject("Unknown media type: " + mediaType);
        });
    }
  };

  /**
   * 停止接收远端媒体
   * @param {*} userId 
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  @tracelog
  stopReceiveMedia(userId, mediaType, mediaId) {   
    this.cacheSendReceiveMap.delete(getEventPostfix(userId, mediaType, mediaId))
    // this.cacheSendQueue.push({userId, mediaType, mediaId, recv: 0}) 
    switch(mediaType) {
      case MediaType.AUDIO:
        return audioManager.stopReceiveAudio(userId, mediaId);
      case MediaType.VIDEO:
		// 取消视频 去除之前绑定在video标签的东西
        return new Promise((resolve, reject) => {
          videoManager.stopReceiveVideo(userId, mediaId).then((data) => {
            let ele = this.elementMap.get(getEventPostfix(userId, MediaType.VIDEO, mediaId))
            ele && ele.unRenderMedia()
            resolve(data)
          }).catch((er) => {
            reject(er)
          })
        })
        // return videoManager.stopReceiveVideo(userId, mediaId);
      case MediaType.SCREEN_SHARE:
        return shareManager.stopReceiveScreenShare(userId, mediaId);
      case MediaType.WHITE_BOARD:
        return boardManager.stopReceiveWhiteBoard(userId, mediaId);
      default:
        return new Promise((resolve, reject) => {
          reject("Unknown media type: " + mediaType);
        });
    }
  }

  /**
   * 设置媒体渲染器
   * @param {*} userId      用户ID，可以是本地用户或远端用户 
   * @param {*} mediaType   媒体类型：音频、视频、屏幕共享、电子白板
   * @param {*} mediaId     媒体ID
   * @param {*} htmlEle     HTML标签，对于白板来说是canvas，其它是video
   * @param {*} params      本地渲染有效 
   */
  @tracelog
  setMediaRender(userId, mediaType, mediaId, htmlEle, params) {
    switch(mediaType) {
      case MediaType.AUDIO: 
      case MediaType.VIDEO:
      case MediaType.SCREEN_SHARE:
        this._setMediaRender(userId, mediaType, mediaId, htmlEle, params);
        break;
      case MediaType.WHITE_BOARD:
        boardManager.setWhiteBoardRender(mediaId, htmlEle);
        break;
      default:
        logger.error("Unknown media type: " + mediaType);
        break;
    }
  };

  /**
   * 取消设置媒体渲染器
   * @param {*} userId 
   * @param {*} mediaType FIXME：音频和视频都传入MediaType？
   * @param {*} element 
   */
  @tracelog
  unsetMediaRender(userId, mediaType, htmlEle, mediaId) {
    logger.trace("Calling unsetMediaRender: " + userId + ", " + mediaType);
    switch(mediaType) {
      case MediaType.AUDIO: 
      case MediaType.VIDEO:
        this._unsetMediaRender(userId, htmlEle, mediaType);
        break;
      case MediaType.SCREEN_SHARE:
        shareManager.unsetScreenShareRender(userId, htmlEle);
        break;
      case MediaType.WHITE_BOARD:
        boardManager.unsetWhiteBoardRender(userId, htmlEle);
        break;
      default:
        logger.error("Unknown media type: " + mediaType);
        break;
    }
  };

  /**
   * 查找UpPeerConnection
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  findUpPcByMedia(mediaType, mediaId) {
    for (const [key, value] of this.upPcMap){
      for(const track of value.upTracks) { // 
        if (track.mediaType == mediaType && track.mediaId == mediaId) {
          return value;
        }
      }
    }
    return null;
  }

  /**
   * 查找DownPeerConnection
   * @param {*} userId 
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  findDownPcByUserMeida(userId, mediaType, mediaId) {
    for (const [key, value] of this.downPcMap){
      for (const track of value.downTracks){
        if (track.userId == userId 
          && track.mediaType == mediaType 
          && track.mediaId == mediaId){
          return value;
        }
      }
    }
    return null;
  }

  /**
   * 删除DownPeecConnection
   * @param {*} bindId 
   */
  removeDownPc(bindId) {
    logger.log('delete down peerconnetion: ' + bindId)
    if (!this.downPcMap.delete(bindId)) {
      logger.warn("Cannot find down peer connection by bindId: " + bindId);
    }
  }

  /**
   * 删除UpPeerConnection
   * @param {*} bindId 
   */
  removeUpPc(bindId) {
    if (!this.upPcMap.delete(bindId)) {
      logger.warn("Cannot find up peer connection by bindId: " + bindId);
    }
  }

  /**
   * 销毁所有的PeerConnection
   */
  destroyPeerConnections() {
    for (const pc of this.upPcMap.values()) {
      pc.close();
    }
    this.upPcMap.clear();

    for (const pc of this.downPcMap.values()) {
      pc.doClose();
    }
    this.downPcMap.clear();
  }

    /**
   * 设置视频本地放对象 
   * @param {*} videoEle
   * @param {*} streamId 
   */
  setLocalVideoRender(mediaId, videoEl, videoParam) {
    return videoManager.setLocalVideoRender(mediaId, videoEl, videoParam)
  }
  /**
   * 设置音视频播放对象 
   * @param {*} videoEle
   * @param {*} streamId 
   */
  _setMediaRender(userId, mediaType, mediaId, videoEle, videoParam){
    if (userId == Store.userInfo.userId) { // 本端
      if (mediaType == MediaType.VIDEO) {
        videoManager.setLocalVideoRender(mediaId, videoEle, videoParam);
      } else if (mediaType == MediaType.SCREEN_SHARE) {
        shareManager.setLocalScreenShareRender(videoEle);
      } else {
        logger.error("Unexpected media type: " + mediaType);
      }
    } else { // 远端
      let pc = this.findDownPcByUserMeida(userId, mediaType, mediaId);
      if (pc) {
        let ele = new Element({el: videoEle, userId: userId, mediaId: mediaId, mediaType: mediaType})

        this.elementMap.set(getEventPostfix(userId, mediaType, mediaId), ele)

        ele.renderMedia(pc.mediaStream)
        // videoEle.srcObject = pc.mediaStream;
        pc.videoEle = videoEle;
        if (this.curSpkDevId) { // 同时设置下扬声器
          audioManager.setAudioSpeaker(videoEle);
        }
      } else {
        logger.error("Cannot find down peer connection!");
      }
    }
  };

  /**
   * 取消设置音视频播放对象
   * 音频不需要显式调用unset，本端没有渲染，远端停止接收即可
   * @param {*} userId 
   * @param {*} videoEle 
   */
  _unsetMediaRender(userId, videoEle) {
    if (!videoEle || !('srcObject' in videoEle)){
      logger.error("Invalid videoEle!");
      return;
    }
    if (userId == Store.userInfo.userId) { // 本端视频
      if (videoEle.srcObject) {
          for (const track of videoEle.srcObject.getTracks()) {
            track.stop();
          }
          videoEle.srcObject = null;
      } else {
        logger.warn("Invalid srcObject!");
      }
    } else { // 远端视频
      videoEle.srcObject = null;
    }
  }

  /**
   * 获取流统计数据
   * @param {*} params 
   * @param {*} callback 
   * @param {*} interval 
   * @param {*} userData 
   */
   _doGetStreamStats(params, callback, interval, userData) {
    let pc = null
    let timer = setInterval(() => {
      if (!pc) {
        if (params.userId === Store.userInfo.userId) { // 本地发送的流
          // 本地可能广播的是变声，也可能广播的是原声，查询的时候两者都要考虑
          if (params.mediaType == MediaType.AUDIO) {
            pc = this.findUpPcByMedia(params.mediaType, 'appdef_mic');
            if (!pc) {
              pc = this.findUpPcByMedia(params.mediaType, 'appdef_mic_magic');
            }
          } else {
            pc = this.findUpPcByMedia(params.mediaType, params.mediaId);
          }
        } else { // 接收远端的流
          pc = this.findDownPcByUserMeida(params.userId, params.mediaType, params.mediaId);
        }
      } else {
        let stats = null
        if (params.mediaType == MediaType.AUDIO) {
          stats = pc.getAudioStats()
        } else if (params.mediaType == MediaType.VIDEO || params.mediaType == MediaType.SCREEN_SHARE) {
          stats = pc.getVideoStats()
        } else {
          logger.error("Unexpected media type: " + params.mediaType);
        }
        stats ? callback(stats, userData) : clearInterval(timer)
      }
    }, interval)
    return {
      stop () {
        clearInterval(timer)
      }
    }
  }

  /**
   * 获取流统计数据
   * @param {*} params    媒体参数，指定针对哪一路媒体流进行统计
   * @param {*} callback  回调函数
   * @param {*} interval  回调间隔（单位ms）
   * @param {*} userData  用户定义参数，回调时携带
   */
  @tracelog
  getStreamStats(params, callback, interval, userData) {
    if (!checkParam(params) 
      || !checkParam(params.userId) 
      || !checkParam(params.mediaType) 
      || !checkParam(params.mediaId)) {
      logger.error("Invalid params!")
      return
    }

    if (!callback) {
      logger.error("Invalid callback!");
      return
    }

    if (!interval) { // 默认回调间隔1秒
      interval = 1000
    }

    // 本意是想增加一个tryCount来限制获取流统计的超时时间，目前看好像不需要
    // 后续如果有需要，可以在_doGetStreamStats增加一个tryCount参数
    return this._doGetStreamStats(params, callback, interval, userData)
  };

  /**
   * 按照从webrtc获取的实际结果返回设备列表
   * @param {Array} devices 
   */
  _getMediaDeivicesWithLabel(devices) {
    let mediaDevs = {
      camDevs: [],
      micDevs: [],
      spkDevs: []
    };
    
    for (const dev of devices) {
      if (dev.kind === 'videoinput') {
        mediaDevs.camDevs.push({
          devName: dev.label, 
          devId: MediaMapping.mappingDevIdToMediaId(MediaType.VIDEO, dev.deviceId)
        });
      } else if (dev.kind === 'audioinput') {
        if(dev.deviceId !== 'default' && dev.deviceId !== 'communications') {
          mediaDevs.micDevs.push({
            devName: dev.label, 
            devId: MediaMapping.mappingDevIdToMediaId(MediaType.AUDIO, dev.deviceId)
          });
        }
      } else if (dev.kind === 'audiooutput') {
        if(dev.deviceId !== 'default' && dev.deviceId !== 'communications') {
          mediaDevs.spkDevs.push({devName: dev.label, devId: dev.deviceId});
        }
      } else {
        logger.warn("Ingnored device kind: " + dev.kind);
      }
    }
    logger.log(JSON.stringify(mediaDevs));
    return mediaDevs;
  }

  /**
   * 如果有label为空，则序号瓶装devName
   * @param {*} devices 
   */
  _tryGetMediaDevicesWithLabel(devices) {
    let mediaDevs = {
      camDevs: [],
      micDevs: [],
      spkDevs: []
    };

    // 从1开始编号
    let camNumber = 1;
    let micNumber = 1;
    let spkNumber = 1;

    for (const dev of devices) {
      if (dev.kind === 'videoinput' && checkParam(dev.deviceId)) {
        if (!dev.label) {
          mediaDevs.camDevs.push({
            devName: "camera " + camNumber, 
            devId: MediaMapping.mappingDevIdToMediaId(MediaType.VIDEO, dev.deviceId)
          }); 
          camNumber++;
        } else {
          mediaDevs.camDevs.push({
            devName: dev.label, 
            devId: MediaMapping.mappingDevIdToMediaId(MediaType.VIDEO, dev.deviceId)
          });
        }
      } else if (dev.kind === 'audioinput' && checkParam(dev.deviceId)) {
        if(dev.deviceId !== 'default' && dev.deviceId !== 'communications') {
          if (!dev.label) {
            mediaDevs.micDevs.push({
              devName: "microphone " + micNumber, 
              devId: MediaMapping.mappingDevIdToMediaId(MediaType.AUDIO, dev.deviceId)
            });
            micNumber++;  
          } else {
            mediaDevs.micDevs.push({
              devName: dev.label, 
              devId: MediaMapping.mappingDevIdToMediaId(MediaType.AUDIO, dev.deviceId)
            });
          }
        }
      } else if (dev.kind === 'audiooutput' && checkParam(dev.deviceId)) {
        if(dev.deviceId !== 'default' && dev.deviceId !== 'communications') {
          if (!dev.label) {
            mediaDevs.spkDevs.push({
              devName: "speaker " + spkNumber, 
              devId: dev.deviceId
            });
            spkNumber++;
          } else {
            mediaDevs.spkDevs.push({
              devName: dev.label, 
              devId: dev.deviceId
            });
          }
        }
      } else {
        logger.warn("Ingnored device kind: " + dev.kind);
      }
    }
    logger.log(JSON.stringify(mediaDevs));
    return mediaDevs;
  }

  /**
   * 判断是否包含有效的label
   * @param {*} devices 
   */
  _isMediaDevicesHasLabel(devices) {
    let hasLabel = true;
    for (const dev of devices) {
      if (dev.label === "") {
        hasLabel = false;
        break;
      }
    }
    return hasLabel;
  }

  /**
   * audio和video要分开弹框，否则，如果不存在音频或视频设备，getUserMedia会
   * 抛异常，这样已存在的设备也无法打开，也就无法获取到设备权限。
   * 这里使用await是为了保证获取到设备访问权限后再去枚举设备。
   */
  async _tryEnumerateDevices() {
    try {
      let stream = await navigator.mediaDevices.getUserMedia({audio:true});
      for (const track of stream.getTracks()) { // 立即关闭设备
        track.stop();
      }
    } catch(error) {
      logger.warn("Enumerate audio devices failed: " + error);
    }

    try {
      let stream = await navigator.mediaDevices.getUserMedia({video:true});
      for (const track of stream.getTracks()) { // 立即关闭设备
        track.stop();
      }
    } catch (error) {
      logger.warn("Enumerate video devices failed: " + error);
    }

    return await navigator.mediaDevices.enumerateDevices();
  }

  /**
   * 获取媒体设备列表，获取相关权限后才能获得设备的label
   */
  getMediaDevices() {
    let self = this;
    return new Promise((resolve, reject) => {
      // 先尝试直接获取媒体设备
      navigator.mediaDevices.enumerateDevices().then(devices => {
        //logger.log(JSON.stringify(devices));
        // 已经有label，则直接获取设备列表
        if (self._isMediaDevicesHasLabel(devices)) { 
          resolve(self._getMediaDeivicesWithLabel(devices));
        } else { // label为空，需要先获取设备权限
          self._tryEnumerateDevices().then((devices) => {
            resolve(self._tryGetMediaDevicesWithLabel(devices));
          }).catch(err => {
            logger.error(err);
            reject(err);
          })
        }
      }).catch(err => { 
        logger.error(err);
        reject(err); 
      })
    });
  }

  /**
   * 通知有用户加入分组
   * @param {*} msg 
   * @param {*} socket 
   */
  onNotifyUserJoinGroup(msg, socket) {
    eventRegistry.trigger("onUserJoinGroup", msg.user_id);
  }

  /**
   * 通知有用户退出分组
   * @param {*} msg 
   * @param {*} socket 
   */
  onNotifyUserLeaveGroup(msg, socket) {
    this._stopReceiveAllMedia(msg.user_id) // fix bug #2202
    eventRegistry.trigger("onUserLeaveGroup", msg.user_id);
  }

  _stopReceiveAllMedia (userId) {
    let userMediaInfo = this.groupUsersMap.get(userId)
    if (userMediaInfo) {
      userMediaInfo.forEach((item) => {
        // 白板不属于个人 个人退出分组，不做取消订阅处理
        if (item.mediaType !== MediaType.WHITE_BOARD) {
          let { userId, mediaType, mediaId } = item
          this.stopReceiveMedia(userId, mediaType, mediaId)
        }
      })
    }
  
  }

  /**
   * 构造事件类型
   * @param {*} operation 
   * @param {*} userId 
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  _getEventType(operation, userId, mediaType, mediaId) {
    return (
      (operation == RecvState.START_RECV) 
        ? EventType.EVENT_START_RCV_MEDIA 
        : EventType.EVENT_STOP_RCV_MEDIA
      ) + getEventPostfix(userId, mediaType, mediaId)
  }

  /**
   * 开始/停止接收音视频响应
   * @param {*} data 
   * @param {*} socket 
   */
  onRecvStreamRes(data, socket) {
    try {
      if (data.group_id.length === 0 || data.media_id.length === 0) {
        throw ("Invalid recv stream response!!!" + data);
      }
  
      // 解析websocket服务地址
      let streamServer = null;
      if (data.stream_server) {
        streamServer = getAddrs("ws", data.stream_server);
        if (!streamServer) {
          throw ("Cannot find webrtc gateway: " + data.stream_server);
        }
      } else if (data.recv === RecvState.START_RECV) { // 接收情况下且无地址 报错
        logger.error('Invalid stream server!')
        return eventRegistry.trigger(
          this._getEventType(data.recv, data.user_id, data.media_type, data.media_id),
          EventResult.NOADDR);
      }
  
      if (data.media_type !== MediaType.WHITE_BOARD) {
        let downPc = this.downPcMap.get(data.bind_id);
        if (data.recv === RecvState.STOP_RECV) {
          if (downPc) {
            downPc.stopRecvStream({
              userId: data.user_id,
              mediaType: data.media_type,
              mediaId: data.media_id
            });
          } else {
            throw ("Have not recv the media, do nothing!");
          }
        } else if (data.recv === RecvState.START_RECV) { 
          logger.log('Current down peer connenction bindid = ' + data.bind_id)
          if (!downPc) {
            downPc = new DownPeerConnection(data.bind_id, this);
            this.downPcMap.set(data.bind_id, downPc);
            logger.log("Add a down peer connection, count = " + this.downPcMap.size);
          }
          if (!data.stream_id) return logger.error('Stream_id is must')
          downPc.startRecvStream({
            userId: data.user_id,
            mediaType: data.media_type,
            mediaId: data.media_id,
            streamId: data.stream_id,
            streamServer: streamServer, 
            streamToken: data.subscribe_token
          });
        } else {
          throw ("Unknown media operation: " + data.recv);
        }
      } else { // 白板
        if (data.recv === RecvState.START_RECV) {
          boardManager.onStartRecvWhiteBoardRes({
            userId: data.user_id,
            mediaType: data.media_type,
            mediaId: data.media_id,
            streamId: data.stream_id,
            streamServer: streamServer, 
            streamToken: data.subscribe_token
          });
        } else {
          boardManager.onStopRecvWhiteBoardRes({
            userId: data.user_id,
            mediaType: data.media_type,
            mediaId: data.media_id,
            streamId: data.stream_id,
          });
        }
      }
      eventRegistry.trigger(
        this._getEventType(data.recv, data.user_id, data.media_type, data.media_id),
        EventResult.SUCCESS);
    } catch (err) {
      eventRegistry.trigger(
        this._getEventType(data.recv, data.user_id, data.media_type, data.media_id),
        EventResult.FAILED);
    }
  }
  /**
   * 通知分组内其他用户媒体状态
   * @param {*} data 
   * @param {*} socket 
   */
  onNotifyUserMediaState(data, socket) {
    if (data.media_type != MediaType.SCREEN_SHARE
      && data.media_type != MediaType.AUDIO
      && data.media_type != MediaType.VIDEO
      && data.media_type != MediaType.WHITE_BOARD){
      logger.warn("Unexpected media type: ", data.media_type);
      return;
    }
    let params = {
      userId: data.user_id,
      mediaType: data.media_type,
      mediaId: data.media_id,
      mediaName: data.media_description
    };
   
    // 通知用户广播/停止广播
    if (data.operation == MediaOper.STOP_PUBLISH){
      this._deleteUserMediaInfo(params)
      eventRegistry.trigger("onUnPublishMedia", params);
    } else if (data.operation == MediaOper.START_PUBLISH) {
      this._storeUserMediaInfo(params)
      eventRegistry.trigger("onPublishMedia", params);
    }
  }
  // 删除保存的用户媒体相关信息
  _deleteUserMediaInfo (params) {
    let userMediaInfo = this.groupUsersMap.get(params.userId);
    if (userMediaInfo) {
      for (let [item, index] of userMediaInfo.entries()) {
        if (item.userId === params.userId && item.mediaType === params.mediaType && item.mediaId === params.mediaId) {
          userMediaInfo.splice(index, 1);
          break
        }
      }
      this.groupUsersMap.set(userMediaInfo)
    }
  }
  /**
   * 保存用户媒体相关信息
   * @param {*} params 
   */
  _storeUserMediaInfo (params) {
    let userMediaInfo = this.groupUsersMap.get(params.userId);
    if (userMediaInfo) {
      userMediaInfo.push(params);
    } else {
      this.groupUsersMap.set(params.userId, [params]);
    }
  }

  /**
   * 通知分组中全量用户及其媒体状态
   * @param {*} data 
   * @param {*} socket 
   */
  onNotifyGroupMediaStates(data, socket) {
    // FIXME：在恢复的过程中，有可能媒体状态发生了改变，这里简化处理了
    if (Store.restoring) {
      Store.restoring = false
      return
    }
    
    let groupUsers = [];
    for (const user of data.user_info) {
      groupUsers.push(user.user_id);
    }

    // 先通知分组用户列表
    if (groupUsers.length > 0){
      eventRegistry.trigger("onGroupUserList", groupUsers);
    }

    // 然后再通知媒体广播
    for (const user of data.user_info) {
      if (user.media_info.length <= 0) { 
        continue; 
      }
    
      for (const media of user.media_info) {
        let params = {
          userId: user.user_id,
          mediaType: media.media_type,
          mediaId: media.media_id,
          mediaName: media.media_description
        };
        this._storeUserMediaInfo(params)
        eventRegistry.trigger("onPublishMedia", params);
      }
    }

    // 通知白板广播
    for (const media of data.group_media) {
      let params = {
        userId: media.user_id,
        mediaType: media.media_type,
        mediaId: media.media_id,
        mediaName: media.media_name
      };
      this._storeUserMediaInfo(params)
      eventRegistry.trigger("onPublishMedia", params);
    }
  }

  /**
   * 通知发送流
   * @param {*} data 
   * @param {*} socket 
   */
  onNotifySendStream(data, socket) {
    if (data.media_type == undefined || data.media_id == undefined) {
      logger.error("Invalid data: " + data);
      return;
    }

    if (data.send === 1) {
      this._notifyStartSendStream(data, socket);
    } else if (data.send === 0) {
      this._notifyStopSendStream(data, socket);
    }
  }

  /**
   * 服务器通知开始发送流处理
   * @param {*} data 
   * @param {*} socket 
   */
  _notifyStartSendStream(data, socket) {
    let pc = this.upPcMap.get(data.bind_id);

    // 解析websocket服务地址
    let streamServer = getAddrs("ws", data.stream_server);
    if (!streamServer) {
      logger.error("Cannot find webrtc gateway: " + data.stream_server);
      return;
    }

    // 音频和视频可能复用同一个PeerConnection
    if (!pc ) {
      logger.log("Add up peer connection.");
      pc = new UpPeerConnection(data.bind_id);
      this.upPcMap.set(data.bind_id, pc);
    } else {
      // fix bug 偶现peerconntion协商过程中服务器（状态为connected 正常） 无返回 一直通知发流的情况
      if ((pc.mediaType === data.media_type && pc.mediaId === data.meida_id) || (pc.state === 'errored' && pc.state)) { // type相同 表示重复通知发流 原pc无效
        pc = new UpPeerConnection(data.bind_id)
        this.upPcMap.set(data.bind_id, pc)
      } else {
        logger.warn("Up peer connection exists!!!")
      }
    }

    pc.startSendStream({
      streamServer: streamServer, 
      streamId: data.stream_id,
      streamToken: data.publish_token,
      mediaType: data.media_type,
      mediaId: data.media_id
    });
  }

  /**
   * 服务器通知停止发送流处理
   * @param {*} data 
   * @param {*} socket 
   */
  _notifyStopSendStream(data, socket) {
    let pc = this.upPcMap.get(data.bind_id);
    if (!pc) {
      logger.log("Cannot find peer connection!");
      return;
    }

    pc.stopSendStream({
      mediaType: data.media_type,
      mediaId: data.media_id
    });
  }  

  /**
   * 构造媒体constraints
   * @param {*} params 
   */
  @tracelog
  constructConstraints(params) {
    // 将外部可见的mediaId转换为devId
    let devId = MediaMapping.getMappingDevId(params.mediaType, params.mediaId);
    if (!devId) {
      logger.error("Cannot find mapping devId!");
      return null;
    }

    if (params.mediaType == MediaType.AUDIO) { // 音频
      return audioManager.buildConstraints(devId);
    } else if (params.mediaType == MediaType.VIDEO) {
      return videoManager.buildConstraints(params.mediaId, devId);
    } else if (params.mediaType == MediaType.SCREEN_SHARE) {
      return shareManager.buildConstraints();
    } else {
      logger.error("Unexpected media type: " + params.mediaType);
      return null;
    }
  }

  /**
   * 切换设备
   * @param {*} mediaType 切换媒体类型
   * @param {*} forId 切换流的meidaId
   * @param {*} toId 切换到对应的devid
   */
  @tracelog
  switchDevice (mediaType, forId, toId, videoParam) {
    if (mediaType === MediaType.AUDIO) {
      return this._switchAudio(forId, toId, videoParam)
    } else if(mediaType === MediaType.VIDEO) {
      return this._switchCamera(forId, toId, videoParam)
    } else {
      return Promise.reject('Invalid mediaType, supprot 1 or 2')
    }
  }
  /**
   * 切换摄像头
   * @param {*} meidaId 
   * @param {*} devId 
   */
  _switchCamera ( mediaId, devId, videoParam) {
    console.log("Switch " + mediaId + " camera to " + devId);
    return new Promise((resolve, reject) => {
      if (!(mediaId || !devId)) {
        reject("Invalid params!");
        return;
      }
      mediaMapping.setMediaIdToDevIdMap(MediaType.VIDEO, mediaId, devId)

      //保存当前切换分辨率
      if(videoParam) videoManager.setVideoParam(mediaId, videoParam);

      let pc = this.findUpPcByMedia(MediaType.VIDEO, mediaId);

      if (pc) {
        if (pc.state !== 'connected') { // peerconntion不正常 重新连接
          pc.close()
        } else {
          pc.switchDevice(MediaType.VIDEO, videoParam);
        }
      }
      resolve();
    });
  }
  _switchAudio (mediaId, devId) {
    return new Promise((resolve, reject) => {
      if (!(mediaId || !devId)) {
        reject("Invalid params!");
      }
      // 如果当前curMicDevId 不为空为pass系统 不支持切换
      if (audioManager.curMicDevId !== null) {
        reject("switch audio is not support!")
      }
      mediaMapping.setMediaIdToDevIdMap(MediaType.AUDIO, mediaId, devId)
      let pc = this.findUpPcByMedia(MediaType.AUDIO, mediaId);
      if (pc) {
        pc.switchDevice(MediaType.AUDIO);
      }
      resolve()
    })
  }
  /** 上报当前渲染流窗口大小
   * 通知 webgw 当前接收视频的窗口的宽高，适配svc, 切换收流的分辨率
   * @param {*} userId      用户ID，远端用户 
   * @param {*} mediaId     媒体ID
   * @param {*} width       video窗口的宽度
   * @param {*} height      video窗口的高度
   */
  reportDisplaySize(userId, mediaId, width, height) {
    let params = {
      width: width,
      height: height,
      streamId: null
    };
    let pc = this.findDownPcByUserMeida(userId, MediaType.VIDEO, mediaId);
    if (pc) {
      for(let i = 0;i< pc.downTracks.length; i++) {
        if (pc.downTracks[i].userId == userId) {
          params.streamId = pc.downTracks[i].streamId
          break
        }
      }
      pc.matchResolution(params)
    } else {
      logger.error('Have not found DownPc !')
    }
  }

  switchResolution (mediaId, videoParam) {
     //保存当前切换分辨率
    if(videoParam) videoManager.setVideoParam(mediaId, videoParam);
    let pc = this.findUpPcByMedia(MediaType.VIDEO, mediaId);

    if (pc) {
      return pc.switchResolution(videoParam);
    }
    return Promise.resolve('set resolution success')
  }
}

export default new MediaManager();